import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { CONFIG } from 'variables/config';
import { toast } from 'react-toastify';
import { useIndustries } from 'services/industryService';
import { hostnameFromUrl } from 'services/hostname';
import {
    activityLabel,
    authConfig,
    fetchPages,
    IntegrationPage,
    IntegrationSitePages,
    plural,
    TemplateOverview,
    timeAgo,
} from 'services/integrations';

/**
 * "Pages" tab of a site form (`/integrations/:id?tab=pages`): what each page sells, and what's left
 * to do about it. Built around a to-do (pages with no activity — their leads can't be sold —,
 * detected activities to check, activities with no price), a one-page-at-a-time review mode, inline
 * activity changes, bulk actions, adding pages by hand and removing them, and each site's default
 * activity (a default: pages the seller changed keep their own activity).
 * URL params: site, filter (todo | unclassified | toCheck | verified), activity (industry|sub),
 * section (/panneaux-solaire/ or / for the root), review=1, add=1. See notes/ux-integrations-vendeur.md §13.
 */

type Filter = 'all' | 'todo' | 'unclassified' | 'toCheck' | 'verified';
type Activity = { industry: string; subIndustry: string };
type GroupBy = 'section' | 'site' | 'both';
type TodoGroup = { key: string; hostname: string | null; section: string | null; urls: string[]; sites: number };

const NO_ACTIVITY: Activity = { industry: '', subIndustry: '' };
const pathOf = (url: string) => url.replace(/^https?:\/\/[^/]+/, '') || '/';
// A page's section: its first path segment ("/panneaux-solaire/" for /panneaux-solaire/blog-1), "/" at the root.
const sectionOf = (url: string) => {
    const m = pathOf(url).split(/[?#]/)[0].match(/^\/([^/]+)\//);
    return m ? `/${m[1]}/` : '/';
};
const keyOf = (a: { industry?: string | null; subIndustry?: string | null }) => `${a.industry || ''}|${a.subIndustry || ''}`;
const isToCheck = (p: IntegrationPage) => p.activitySource === 'pixel';
const isUnclassified = (p: IntegrationPage) => !p.industry;

// Words of a slug or label, for matching "/panneaux-solaires/" to "Panneaux solaires": accents
// folded, French stop words dropped, plurals reduced ("solaires" → "solaire").
const STOP_WORDS = new Set(['a', 'au', 'aux', 'de', 'des', 'du', 'd', 'en', 'et', 'l', 'la', 'le', 'les', 'pour', 'sur', 'un', 'une']);
const wordsOf = (text: string) =>
    text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((w) => w && !STOP_WORDS.has(w))
        .map((w) => (w.length > 3 ? w.replace(/[sx]$/, '') : w));
// How alike two sets of words are (Dice coefficient): 1 = same words, 0 = none in common. 0 too
// unless one side's words are all in the other: "assurance-auto" isn't "Assurance habitation".
const likeness = (a: string[], b: string[]) => {
    const common = a.filter((w) => b.includes(w)).length;
    return a.length && b.length && (common === a.length || common === b.length) ? (2 * common) / (a.length + b.length) : 0;
};

const btn = 'rounded-[9px] px-3 py-[7px] font-hanken text-[12.5px] font-semibold transition-colors disabled:opacity-50';
const btnPrimary = `${btn} bg-[#241C5C] text-white hover:bg-[#332a75]`;
const btnGhost = `${btn} border border-[#DDD5C9] bg-white text-[#3B352C] hover:border-[#241C5C]`;
const select = 'rounded-[8px] border border-[#DDD5C9] bg-white px-2 py-1.5 font-hanken text-[13px] outline-none focus:border-[#241C5C]';

// Searchable activity pickers (react-select), sized and colored like the `select` class above.
// The menu is portaled to body so table rows and dialogs don't clip it.
const activitySelectStyles = {
    container: (base: any) => ({ ...base, minWidth: 190 }),
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 32,
        borderRadius: 8,
        borderColor: state.isFocused ? '#241C5C' : '#DDD5C9',
        boxShadow: 'none',
        fontSize: 13,
        '&:hover': { borderColor: '#241C5C' },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0 8px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({ ...base, padding: 4 }),
    placeholder: (base: any) => ({ ...base, color: '#8A8172' }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    menu: (base: any) => ({ ...base, borderRadius: 10, border: '1px solid #EDE7DE', boxShadow: '0 8px 24px rgba(26,22,19,0.12)', fontSize: 13 }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isSelected ? '#241C5C' : state.isFocused ? '#F1ECE4' : 'white',
        color: state.isSelected ? 'white' : '#1A1613',
    }),
};

const StatusChip = ({ page }: { page: IntegrationPage }) => {
    const [label, tone] = !page.industry
        ? ['Sans activité', 'bg-[#FBEDEB] text-[#A8332B]']
        : page.activitySource === 'pixel'
        ? ['À vérifier', 'bg-[#FCF2D4] text-[#7A5A05]']
        : page.activitySource === 'site'
        ? ['Activité du site', 'bg-[#F1ECE4] text-[#6B6357]']
        : page.activitySource === 'default'
        ? ["Définie par l'IA", 'bg-[#F1ECE4] text-[#6B6357]']
        : ['Vérifiée', 'bg-[#D9F0DE] text-[#146334]'];
    return <span className={`whitespace-nowrap rounded-full px-2 py-[2px] font-hanken text-[11.5px] font-semibold ${tone}`}>{label}</span>;
};

const PagesTab = ({
    templateId,
    overview,
    refreshOverview,
    onTemplateChange,
    onOpenPrices,
}: {
    templateId: string;
    overview: TemplateOverview | null;
    refreshOverview: () => void;
    onTemplateChange: (t: any) => void;
    onOpenPrices: () => void;
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { industries } = useIndustries();
    const [pages, setPages] = useState<IntegrationPage[]>([]);
    const [sites, setSites] = useState<IntegrationSitePages[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    // Bulk saves go 500 pages at a time: how far along a long one is.
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkActivity, setBulkActivity] = useState<Activity>(NO_ACTIVITY);
    const [limit, setLimit] = useState(50);
    const [menuFor, setMenuFor] = useState<string | null>(null);
    // The row whose activity is being changed (its pickers are only shown then, to keep rows short).
    const [editing, setEditing] = useState<{ url: string; draft: Activity } | null>(null);

    const site = searchParams.get('site') || '';
    const filter = (searchParams.get('filter') as Filter) || 'all';
    const activityFilter = searchParams.get('activity') || '';
    const section = searchParams.get('section') || '';
    const reviewing = searchParams.get('review') === '1';
    const adding = searchParams.get('add') === '1';

    const setParam = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'pages');
        Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        setSearchParams(next, { replace: true });
    };

    const reload = useCallback(async () => {
        try {
            const data = await fetchPages(templateId);
            setPages(data.pages);
            setSites(data.sites);
        } catch {
            toast.error('Erreur lors du chargement des pages');
        } finally {
            setLoading(false);
        }
    }, [templateId]);
    useEffect(() => {
        reload();
    }, [reload]);

    const afterChange = async (template?: any) => {
        if (template) onTemplateChange(template);
        await reload();
        refreshOverview();
    };

    // Price shown on each row: the form's price for that page's activity (own, else account).
    const priceBy = useMemo(() => {
        const map = new Map<string, { price: number | null; source: string | null }>();
        (overview?.activities || []).forEach((a) => map.set(keyOf(a), { price: a.minimumPrice, source: a.priceSource }));
        return map;
    }, [overview]);

    const inSite = useMemo(() => pages.filter((p) => !site || p.hostname === site), [pages, site]);
    const counts = {
        all: inSite.length,
        unclassified: inSite.filter(isUnclassified).length,
        toCheck: inSite.filter(isToCheck).length,
        verified: inSite.filter((p) => p.verified).length,
    };
    const unpricedActivities = (overview?.activities || []).filter((a) => a.minimumPrice == null && a.pages > 0);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return inSite
            .filter((p) =>
                filter === 'todo'
                    ? isUnclassified(p) || isToCheck(p)
                    : filter === 'unclassified'
                    ? isUnclassified(p)
                    : filter === 'toCheck'
                    ? isToCheck(p)
                    : filter === 'verified'
                    ? p.verified
                    : true
            )
            .filter((p) => !activityFilter || keyOf(p) === activityFilter)
            .filter((p) => !section || sectionOf(p.url) === section)
            .filter((p) => !q || p.url.toLowerCase().includes(q) || activityLabel(p).toLowerCase().includes(q))
            // What needs the seller first: no activity, then to check, then the busiest pages.
            .sort((a, b) => Number(isUnclassified(b)) - Number(isUnclassified(a)) || Number(isToCheck(b)) - Number(isToCheck(a)) || b.views - a.views);
    }, [inSite, filter, activityFilter, section, query]);

    const sections = useMemo(() => {
        const n = new Map<string, number>();
        inSite.forEach((p) => n.set(sectionOf(p.url), (n.get(sectionOf(p.url)) || 0) + 1));
        return Array.from(n.entries()).sort((a, b) => b[1] - a[1]);
    }, [inSite]);

    // Pages left to do, grouped by product section (across sites), by subdomain, or by both: one
    // click selects a whole group for the bulk bar.
    const multiHost = useMemo(() => new Set(inSite.map((p) => p.hostname)).size > 1, [inSite]);
    const [groupBy, setGroupBy] = useState<GroupBy>('section');
    const groupMode: GroupBy = multiHost ? groupBy : 'section';
    const [groupQuery, setGroupQuery] = useState('');
    const [groupLimit, setGroupLimit] = useState(8);
    useEffect(() => setGroupLimit(8), [groupMode, groupQuery, site]);
    const todoGroups = useMemo(() => {
        const groups = new Map<string, TodoGroup & { hosts: Set<string> }>();
        inSite
            .filter((p) => isUnclassified(p) || isToCheck(p))
            .forEach((p) => {
                const sec = sectionOf(p.url);
                const hostname = groupMode === 'section' ? null : p.hostname;
                const section = groupMode === 'site' ? null : sec;
                const key = `${hostname || ''}${section || ''}`;
                const g = groups.get(key) || { key, hostname, section, urls: [], sites: 0, hosts: new Set<string>() };
                g.urls.push(p.url);
                g.hosts.add(p.hostname);
                groups.set(key, g);
            });
        return Array.from(groups.values())
            .map(({ hosts, ...g }) => ({ ...g, sites: hosts.size }))
            .sort((a, b) => b.urls.length - a.urls.length);
    }, [inSite, groupMode]);
    // Sections ("/par-usage/"), subdomains or subdomain + section the seller hid from the
    // suggestions because they make no sense as a product. Per viewer, per integration.
    const hiddenKey = `leadrs.pages.hiddenGroups.${templateId}`;
    const [hiddenGroups, setHiddenGroups] = useState<string[]>(() => {
        try {
            return JSON.parse(localStorage.getItem(hiddenKey) || '[]');
        } catch {
            return [];
        }
    });
    const saveHiddenGroups = (next: string[]) => {
        setHiddenGroups(next);
        try {
            localStorage.setItem(hiddenKey, JSON.stringify(next));
        } catch {
            /* storage unavailable: hidden for this visit only */
        }
    };
    // What the ✕ hides: the section on every site, else the subdomain (or subdomain + root).
    const hideTargetOf = (g: TodoGroup) => (g.section && g.section !== '/' ? g.section : g.key);
    const isHiddenGroup = (g: TodoGroup) =>
        hiddenGroups.includes(g.key) || (!!g.section && hiddenGroups.includes(g.section)) || (!!g.hostname && hiddenGroups.includes(g.hostname));
    const visibleGroups = todoGroups.filter((g) => !isHiddenGroup(g));
    const shownGroups = useMemo(() => {
        const q = groupQuery.trim().toLowerCase();
        const groups = todoGroups.filter((g) => !isHiddenGroup(g));
        return q ? groups.filter((g) => g.key.toLowerCase().includes(q)) : groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [todoGroups, groupQuery, hiddenGroups]);
    const bulkRef = useRef<HTMLDivElement>(null);

    // What's in the selection, for the bulk bar: where it is and what state its pages are in.
    const selectedPages = useMemo(() => pages.filter((p) => selected.has(p.url)), [pages, selected]);
    const selection = useMemo(() => {
        const hosts = Array.from(new Set(selectedPages.map((p) => p.hostname)));
        const secs = Array.from(new Set(selectedPages.map((p) => sectionOf(p.url))));
        const shown = new Set(visible.slice(0, limit).map((p) => p.url));
        return {
            scope: hosts.length === 1 ? (secs.length === 1 ? hosts[0] + secs[0] : hosts[0]) : hosts.length > 1 ? `${hosts.length} sites` : '',
            unclassified: selectedPages.filter(isUnclassified).length,
            toCheck: selectedPages.filter(isToCheck),
            verified: selectedPages.filter((p) => p.verified).length,
            hidden: selectedPages.filter((p) => !shown.has(p.url)).length,
        };
    }, [selectedPages, visible, limit]);
    useEffect(() => {
        if (!selected.size || reviewing || adding) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !e.defaultPrevented) setSelected(new Set());
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selected.size, reviewing, adding]);

    /* ── Mutations ─────────────────────────────────────────────────────────────── */

    const setActivity = async (urls: string[], activity: Activity) => {
        if (!activity.industry || urls.length === 0) return;
        setBusy(true);
        try {
            // The API takes 500 pages per call.
            let template: any;
            for (let i = 0; i < urls.length; i += 500) {
                const res = await axios.patch(
                    `${CONFIG.apiUrl}/form-template/${templateId}/pages/activity`,
                    { landingPages: urls.slice(i, i + 500), industry: activity.industry, subIndustry: activity.subIndustry || undefined },
                    authConfig()
                );
                template = res.data?.template;
                if (urls.length > 500) setProgress({ done: Math.min(i + 500, urls.length), total: urls.length });
            }
            await afterChange(template);
            return true;
        } catch (e: any) {
            toast.error(e?.response?.data?.status || "Erreur lors de l'enregistrement de l'activité");
            return false;
        } finally {
            setBusy(false);
            setProgress(null);
        }
    };

    // "Marquer comme vérifiées": each page keeps the activity it has (one call per activity).
    const confirmPages = async (urls: string[]) => {
        const byActivity = new Map<string, { activity: Activity; urls: string[] }>();
        pages
            .filter((p) => urls.includes(p.url) && p.industry)
            .forEach((p) => {
                const k = keyOf(p);
                const entry = byActivity.get(k) || { activity: { industry: p.industry!, subIndustry: p.subIndustry || '' }, urls: [] };
                entry.urls.push(p.url);
                byActivity.set(k, entry);
            });
        for (const { activity, urls: group } of Array.from(byActivity.values())) {
            if (!(await setActivity(group, activity))) return;
        }
        const skipped = urls.length - Array.from(byActivity.values()).reduce((n, g) => n + g.urls.length, 0);
        toast.success(skipped ? `Pages vérifiées · ${plural(skipped, 'page sans activité ignorée', 'pages sans activité ignorées')}` : 'Pages vérifiées');
        setSelected((s) => {
            const next = new Set(s);
            urls.forEach((u) => next.delete(u));
            return next;
        });
    };

    const removePage = async (page: IntegrationPage) => {
        if (
            !window.confirm(
                `Retirer ${pathOf(page.url)} de vos pages ? Elle ne reviendra pas d'elle-même. Si le formulaire y est encore, il continue de recevoir des leads : retirez-le de la page.`
            )
        )
            return;
        try {
            const res = await axios.patch(`${CONFIG.apiUrl}/form-template/${templateId}/remove-landing-page`, { landingPage: page.url }, authConfig());
            toast.success('Page retirée');
            await afterChange(res.data);
        } catch (e: any) {
            toast.error(e?.response?.data?.status || 'Erreur lors du retrait de la page');
        }
    };

    const importSitemap = async () => {
        setBusy(true);
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/form-template/${templateId}/explore-sitemap`, site ? { siteUrl: site } : {}, authConfig());
            const added = res.data?.added?.length || 0;
            toast.success(added ? `${plural(added, 'page trouvée', 'pages trouvées')} : à vérifier` : 'Aucune nouvelle page dans le sitemap');
            await afterChange(res.data?.template);
            if (added) setParam({ filter: 'toCheck' });
        } catch (e: any) {
            toast.error(e?.response?.data?.status || e?.response?.data?.message || "Le sitemap n'a pas pu être lu");
        } finally {
            setBusy(false);
        }
    };

    /* ── Site default activity ─────────────────────────────────────────────────── */

    const currentSite = sites.find((s) => s.hostname === site);
    const [siteDraft, setSiteDraft] = useState<Activity>(NO_ACTIVITY);
    useEffect(() => {
        setSiteDraft(
            currentSite?.defaultActivity
                ? { industry: currentSite.defaultActivity.industry, subIndustry: currentSite.defaultActivity.subIndustry || '' }
                : NO_ACTIVITY
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [site, currentSite?.defaultActivity?.industry, currentSite?.defaultActivity?.subIndustry]);
    const saveSiteDefault = async (clear = false) => {
        if (!site) return;
        setBusy(true);
        try {
            const res = clear
                ? await axios.delete(`${CONFIG.apiUrl}/form-template/${templateId}/domain-industry`, { ...authConfig(), params: { hostname: site } })
                : await axios.patch(
                      `${CONFIG.apiUrl}/form-template/${templateId}/domain-industry`,
                      { hostname: site, industry: siteDraft.industry, subIndustry: siteDraft.subIndustry || undefined },
                      authConfig()
                  );
            toast.success(clear ? 'Activité par défaut retirée' : `Activité par défaut de ${site} enregistrée`);
            await afterChange(res.data);
        } catch (e: any) {
            toast.error(e?.response?.data?.status || "Erreur lors de l'enregistrement");
        } finally {
            setBusy(false);
        }
    };

    /* ── Activity picker (render helper, not a component: it'd remount on each render) ── */

    // One searchable list of sub-industries (the industry is implied). An industry with none is its
    // own option; a sub-industry name shared by two industries is suffixed with its industry.
    const activityOptions = useMemo(() => {
        const opts = industries.flatMap((i) =>
            i.subIndustries?.length
                ? i.subIndustries.map((sub: any) => ({ value: `${i.name}|${sub.name}`, label: sub.name, industry: i.name, subIndustry: sub.name }))
                : [{ value: `${i.name}|`, label: i.name, industry: i.name, subIndustry: '' }]
        );
        const seen = new Map<string, number>();
        opts.forEach((o) => seen.set(o.label, (seen.get(o.label) || 0) + 1));
        return opts
            .map((o) => (seen.get(o.label)! > 1 && o.subIndustry ? { ...o, label: `${o.label} (${o.industry})` } : o))
            .sort((x, y) => x.label.localeCompare(y.label, 'fr'));
    }, [industries]);

    // Activity suggested for the selection, from its most common sections then subdomains
    // ("/panneaux-solaires/" → Panneaux solaires). Preselected in the bulk bar.
    const suggestion = useMemo(() => {
        if (!selectedPages.length) return null;
        const tally = (keys: string[]) => {
            const n = new Map<string, number>();
            keys.forEach((k) => n.set(k, (n.get(k) || 0) + 1));
            return Array.from(n.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => k);
        };
        const hints = [
            ...tally(selectedPages.map((p) => sectionOf(p.url)).filter((x) => x !== '/')),
            ...tally(selectedPages.map((p) => p.hostname.split('.')).filter((l) => l.length > 2).map((l) => l[0])),
        ];
        const options = activityOptions.map((o) => ({ o, words: wordsOf(o.subIndustry || o.industry) }));
        for (const hint of hints) {
            const words = wordsOf(hint);
            const best = options.map(({ o, words: w }) => ({ o, score: likeness(words, w) })).sort((a, b) => b.score - a.score)[0];
            if (best && best.score >= 0.5) return { activity: { industry: best.o.industry, subIndustry: best.o.subIndustry }, from: hint };
        }
        return null;
    }, [selectedPages, activityOptions]);
    useEffect(() => {
        if (suggestion) setBulkActivity(suggestion.activity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestion && keyOf(suggestion.activity)]);

    const renderActivitySelect = (value: Activity, onChange: (a: Activity) => void, label: string) => (
        <Select
            styles={{ ...activitySelectStyles, container: (base: any) => ({ ...base, minWidth: 240 }) }}
            className="font-hanken"
            menuPortalTarget={document.body}
            noOptionsMessage={() => 'Aucun résultat'}
            aria-label={label}
            placeholder="Rechercher une activité…"
            isSearchable
            options={activityOptions}
            value={activityOptions.find((o) => o.value === keyOf(value)) || null}
            onChange={(o) => onChange(o ? { industry: o.industry, subIndustry: o.subIndustry } : NO_ACTIVITY)}
        />
    );

    /* ── Review mode: one page at a time ───────────────────────────────────────── */

    const queue = useMemo(() => inSite.filter((p) => isUnclassified(p) || isToCheck(p)).sort((a, b) => Number(isUnclassified(b)) - Number(isUnclassified(a)) || b.views - a.views), [inSite]);
    const [reviewIndex, setReviewIndex] = useState(0);
    const [reviewTotal, setReviewTotal] = useState(0);
    const [reviewDone, setReviewDone] = useState(0);
    const [reviewDraft, setReviewDraft] = useState<Activity>(NO_ACTIVITY);
    const [skipped, setSkipped] = useState<Set<string>>(new Set());
    const pending = queue.filter((p) => !skipped.has(p.url));
    const current = reviewing ? pending[0] : undefined;

    useEffect(() => {
        if (reviewing && !loading) {
            setSkipped(new Set());
            setReviewDone(0);
            setReviewIndex(0);
            setReviewTotal(queue.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewing, loading]);
    useEffect(() => {
        if (current) setReviewDraft({ industry: current.industry || '', subIndustry: current.subIndustry || '' });
    }, [current?.url]); // eslint-disable-line react-hooks/exhaustive-deps

    const reviewConfirm = async () => {
        if (!current || !reviewDraft.industry || busy) return;
        if (await setActivity([current.url], reviewDraft)) {
            setReviewDone((n) => n + 1);
            setReviewIndex((i) => i + 1);
        }
    };
    const reviewSkip = () => {
        if (!current) return;
        setSkipped((s) => new Set(s).add(current.url));
        setReviewIndex((i) => i + 1);
    };
    useEffect(() => {
        if (!reviewing) return;
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            // An activity picker handled the key itself (Enter picks an option, Escape closes its menu).
            if (e.defaultPrevented) return;
            if ((target?.tagName === 'SELECT' || target?.tagName === 'INPUT') && e.key !== 'Enter' && e.key !== 'Escape') return;
            if (e.key === 'Enter') {
                e.preventDefault();
                reviewConfirm();
            } else if (e.key === 'ArrowRight') {
                reviewSkip();
            } else if (e.key === 'Escape') {
                setParam({ review: null });
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    /* ── Add pages ─────────────────────────────────────────────────────────────── */

    const [addSite, setAddSite] = useState('');
    const [addText, setAddText] = useState('');
    const [addActivity, setAddActivity] = useState<Activity>(NO_ACTIVITY);
    // Prefilled once the pages are loaded (the dialog can be opened straight from a link, add=1).
    useEffect(() => {
        if (!adding || loading) return;
        const host = site || sites[0]?.hostname || '';
        setAddSite(host);
        setAddText('');
        // Prefill: the site's default activity, else the activity most of its pages sell.
        const def = sites.find((s) => s.hostname === host)?.defaultActivity;
        if (def) {
            setAddActivity({ industry: def.industry, subIndustry: def.subIndustry || '' });
        } else {
            const tally = new Map<string, { a: Activity; n: number }>();
            pages
                .filter((p) => p.hostname === host && p.industry)
                .forEach((p) => {
                    const k = keyOf(p);
                    const t = tally.get(k) || { a: { industry: p.industry!, subIndustry: p.subIndustry || '' }, n: 0 };
                    t.n += 1;
                    tally.set(k, t);
                });
            const top = Array.from(tally.values()).sort((x, y) => y.n - x.n)[0];
            setAddActivity(top ? top.a : NO_ACTIVITY);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adding, loading]);

    const toUrl = (line: string) => {
        const raw = line.trim();
        if (!raw) return '';
        if (/^https?:\/\//i.test(raw)) return raw;
        if (raw.includes('.') && !raw.startsWith('/')) return `https://${raw}`;
        return addSite ? `https://${addSite}${raw.startsWith('/') ? raw : `/${raw}`}` : '';
    };
    const addUrls = addText.split('\n').map(toUrl).filter(Boolean);
    const addPages = async () => {
        if (!addActivity.industry) {
            toast.error('Choisissez une activité');
            return;
        }
        if (addUrls.length === 0) {
            toast.error('Indiquez au moins une adresse de page');
            return;
        }
        setBusy(true);
        let lastTemplate: any = null;
        let ok = 0;
        for (const url of addUrls) {
            try {
                const res = await axios.patch(
                    `${CONFIG.apiUrl}/form-template/${templateId}/add-landing-page`,
                    { landingPage: url, industry: addActivity.industry, subIndustry: addActivity.subIndustry || undefined },
                    authConfig()
                );
                lastTemplate = res.data;
                ok += 1;
            } catch (e: any) {
                toast.error(`${url} : ${e?.response?.data?.status || 'non ajoutée'}`);
            }
        }
        setBusy(false);
        if (ok) {
            toast.success(`${plural(ok, 'page ajoutée', 'pages ajoutées')} · ${activityLabel(addActivity)}`);
            setParam({ add: null, site: hostnameFromUrl(addUrls[0]) || addSite });
            await afterChange(lastTemplate);
        }
    };

    /* ── Rendering ─────────────────────────────────────────────────────────────── */

    if (loading) return <div className="py-16 text-center font-hanken text-[14px] text-[#8A8172]">Chargement des pages…</div>;

    // Bulk shortcuts from the to-do: show that group and select all of it, so the bulk bar is ready.
    const selectGroup = (key: 'unclassified' | 'toCheck') => {
        setParam({ filter: key, activity: null, section: null });
        setQuery('');
        setSelected(new Set(inSite.filter(key === 'unclassified' ? isUnclassified : isToCheck).map((p) => p.url)));
    };
    const confirmAllToCheck = () => {
        if (!window.confirm(`Valider l'activité détectée sur ${plural(counts.toCheck, 'page', 'pages')} ?`)) return;
        confirmPages(inSite.filter(isToCheck).map((p) => p.url));
    };
    const selectTodoGroup = (g: TodoGroup) => {
        setParam({ site: g.hostname || site || null, section: g.section, filter: 'todo', activity: null });
        setQuery('');
        setSelected(new Set(g.urls));
        setTimeout(() => bulkRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };

    type TodoAction = { label: string; onClick: () => void; primary?: boolean };
    const todo = [
        counts.unclassified > 0 && {
            tone: 'bg-[#A8332B]',
            text: `${plural(counts.unclassified, 'page sans activité', 'pages sans activité')} : ses leads ne peuvent pas être vendus`,
            actions: [
                counts.unclassified > 1 && { label: 'Mettre la même activité sur toutes les pages', onClick: () => selectGroup('unclassified'), primary: true },
                { label: 'Traiter une à une →', onClick: () => setParam({ review: '1' }), primary: counts.unclassified === 1 },
            ],
        },
        counts.toCheck > 0 && {
            tone: 'bg-[#D9A100]',
            text: `${plural(counts.toCheck, 'page à vérifier', 'pages à vérifier')} : l'activité a été détectée automatiquement`,
            actions: [
                { label: counts.toCheck > 1 ? `Tout valider (${counts.toCheck})` : 'Valider', onClick: confirmAllToCheck, primary: true },
                counts.toCheck > 1 && { label: 'Changer l’activité de toutes', onClick: () => selectGroup('toCheck') },
                { label: 'Vérifier une à une →', onClick: () => setParam({ review: '1' }) },
            ],
        },
        unpricedActivities.length > 0 && {
            tone: 'bg-[#D9A100]',
            text: `${plural(unpricedActivities.length, 'activité sans prix', 'activités sans prix')} sur ce formulaire`,
            actions: [{ label: 'Fixer les prix →', onClick: onOpenPrices, primary: true }],
        },
    ]
        .filter(Boolean)
        .map((t: any) => ({ ...t, actions: t.actions.filter(Boolean) })) as { tone: string; text: string; actions: TodoAction[] }[];

    const allVisibleSelected = visible.length > 0 && visible.slice(0, limit).every((p) => selected.has(p.url));
    const someVisibleSelected = visible.slice(0, limit).some((p) => selected.has(p.url));
    const wholeViewSelected = allVisibleSelected && visible.every((p) => selected.has(p.url));
    const checkbox = 'h-4 w-4 cursor-pointer accent-[#241C5C]';
    const chip = (key: Filter, label: string, n?: number) => (
        <button
            key={key}
            onClick={() => setParam({ filter: key === 'all' ? null : key })}
            aria-pressed={filter === key}
            className={`rounded-full border px-3 py-1 font-hanken text-[12.5px] font-semibold transition-colors ${
                filter === key ? 'border-[#241C5C] bg-[#241C5C] text-white' : 'border-[#DDD5C9] bg-white text-[#3B352C] hover:border-[#241C5C]'
            }`}
        >
            {label}
            {n !== undefined ? ` ${n}` : ''}
        </button>
    );

    return (
        <div className="mt-6 flex flex-col gap-4 font-hanken text-[#1A1613]">
            {/* Header: actions */}
            <div className="flex flex-wrap items-center justify-end gap-3">
                <div className="flex flex-wrap gap-2">
                    <button onClick={importSitemap} disabled={busy || sites.length === 0} className={btnGhost}>
                        Importer le sitemap
                    </button>
                    <button onClick={() => setParam({ add: '1' })} className={btnPrimary}>
                        + Ajouter une page
                    </button>
                </div>
            </div>

            {/* To-do */}
            {todo.length > 0 ? (
                <div className="overflow-hidden rounded-[16px] border border-[#F0E4C4] bg-[#FCF9EF]">
                    <p className="px-4 pt-3 text-[11.5px] font-bold uppercase tracking-[0.07em] text-[#A79E90]">À faire{site ? ` sur ${site}` : ''}</p>
                    {todo.map((t) => (
                        <div key={t.text} className="flex flex-wrap items-center gap-3 border-b border-[#F0E4C4] px-4 py-2.5 last:border-0">
                            <span className={`h-2 w-2 shrink-0 rounded-full ${t.tone}`} />
                            <span className="min-w-[200px] flex-1 text-[13.5px] text-[#3B352C]">{t.text}</span>
                            <span className="flex flex-wrap gap-2">
                                {t.actions.map((a) => (
                                    <button key={a.label} onClick={a.onClick} disabled={busy} className={a.primary ? btnPrimary : btnGhost}>
                                        {a.label}
                                    </button>
                                ))}
                            </span>
                        </div>
                    ))}
                    {todoGroups.length > 0 && counts.unclassified + counts.toCheck > 1 && (
                        <div className="border-t border-[#F0E4C4] px-4 py-3">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-[240px] flex-1">
                                    <p className="text-[13.5px] font-semibold text-[#3B352C]">Allez plus vite : attribuez une activité à tout un groupe de pages</p>
                                    <p className="mt-0.5 text-[12.5px] text-[#6B6357]">
                                        Vos {counts.unclassified + counts.toCheck} pages à faire forment {plural(visibleGroups.length, 'groupe', 'groupes')}. Sélectionnez-en un, puis
                                        choisissez son activité.
                                    </p>
                                </div>
                                {multiHost && (
                                    <div role="group" aria-label="Regrouper par" className="flex rounded-[9px] border border-[#DDD5C9] bg-white p-0.5">
                                        {(
                                            [
                                                ['section', 'Par section'],
                                                ['site', 'Par sous-domaine'],
                                                ['both', 'Sous-domaine + section'],
                                            ] as [GroupBy, string][]
                                        ).map(([k, label]) => (
                                            <button
                                                key={k}
                                                onClick={() => setGroupBy(k)}
                                                aria-pressed={groupMode === k}
                                                className={`rounded-[7px] px-2.5 py-1 text-[12px] font-semibold transition-colors ${
                                                    groupMode === k ? 'bg-[#241C5C] text-white' : 'text-[#6B6357] hover:text-[#241C5C]'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {(todoGroups.length > 8 || site) && (
                                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                                    {todoGroups.length > 8 && (
                                        <input
                                            value={groupQuery}
                                            onChange={(e) => setGroupQuery(e.target.value)}
                                            placeholder="Filtrer les groupes (ex. pompe, paris)"
                                            aria-label="Filtrer les groupes"
                                            className="min-w-[240px] rounded-[9px] border border-[#DDD5C9] bg-white px-3 py-1.5 text-[13px] outline-none focus:border-[#241C5C]"
                                        />
                                    )}
                                    {site && (
                                        <button onClick={() => setParam({ site: null, section: null })} className="text-[12.5px] font-semibold text-[#241C5C] hover:underline">
                                            ← Groupes de tous les sites
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="mt-2.5 flex flex-col gap-1.5">
                                {shownGroups.slice(0, groupLimit).map((g) => (
                                    <div key={g.key} className="flex flex-wrap items-center gap-3 rounded-[10px] border border-[#F0E4C4] bg-white px-3 py-2">
                                        <span className="min-w-[200px] flex-1 truncate font-jbmono text-[12.5px] text-[#3B352C]" title={g.key}>
                                            {g.hostname}
                                            {g.section && <strong className="text-[#241C5C]">{g.section}</strong>}
                                            {g.section === '/' && <span className="font-hanken text-[#8A8172]"> (racine du site)</span>}
                                            {!g.hostname && g.sites > 1 && <span className="font-hanken text-[#8A8172]"> · sur {g.sites} sites</span>}
                                        </span>
                                        <span className="text-[12.5px] text-[#6B6357]">{plural(g.urls.length, 'page à faire', 'pages à faire')}</span>
                                        <button onClick={() => selectTodoGroup(g)} disabled={busy} className={btnPrimary}>
                                            Sélectionner et attribuer
                                        </button>
                                        <button
                                            onClick={() => saveHiddenGroups([...hiddenGroups, hideTargetOf(g)])}
                                            aria-label={`Masquer ${hideTargetOf(g)} des suggestions`}
                                            title={`Masquer ${hideTargetOf(g)} des suggestions`}
                                            className="-mr-1 flex h-7 w-7 items-center justify-center rounded-[7px] text-[15px] leading-none text-[#A79E90] transition-colors hover:bg-[#F1ECE4] hover:text-[#3B352C]"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                                {shownGroups.length === 0 && <p className="py-2 text-[12.5px] text-[#8A8172]">Aucun groupe ne correspond.</p>}
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                                {shownGroups.length > groupLimit && (
                                    <button onClick={() => setGroupLimit((l) => l + 20)} className="text-[12.5px] font-semibold text-[#241C5C] hover:underline">
                                        Afficher {Math.min(20, shownGroups.length - groupLimit)} groupes de plus · {shownGroups.length - groupLimit} restants
                                    </button>
                                )}
                                {hiddenGroups.length > 0 && (
                                    <span className="text-[12px] text-[#8A8172]">
                                        Masqués : <span className="font-jbmono">{hiddenGroups.slice(0, 3).join(', ')}{hiddenGroups.length > 3 ? '…' : ''}</span>{' '}
                                        <button onClick={() => saveHiddenGroups([])} className="font-semibold text-[#241C5C] hover:underline">
                                            Tout réafficher
                                        </button>
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                pages.length > 0 && (
                    <div className="flex items-center gap-2.5 rounded-[14px] border border-[#CDEAD8] bg-[#F2FAF4] px-4 py-3 text-[13.5px] text-[#146334]">
                        <span className="h-2 w-2 rounded-full bg-[#1E9E52]" />
                        Toutes vos pages ont une activité vérifiée{site ? ` sur ${site}` : ''}.
                    </div>
                )
            )}

            {/* Site default activity */}
            {currentSite && (
                <div className="flex flex-wrap items-center gap-2.5 rounded-[14px] border border-[#EDE7DE] bg-white px-4 py-3">
                    <span className="min-w-[220px] flex-1 text-[13px] text-[#6B6357]">
                        <strong className="text-[#3B352C]">Activité par défaut de {site}</strong> : appliquée aux pages que vous n'avez pas
                        changées. Une page modifiée garde son activité.
                    </span>
                    {renderActivitySelect(siteDraft, setSiteDraft, `Activité par défaut de ${site}`)}
                    <button onClick={() => saveSiteDefault()} disabled={busy || !siteDraft.industry} className={btnPrimary}>
                        Enregistrer
                    </button>
                    {currentSite.defaultActivity && (
                        <button onClick={() => saveSiteDefault(true)} disabled={busy} className="text-[12.5px] font-semibold text-[#8A8172] hover:text-[#A8332B]">
                            Retirer
                        </button>
                    )}
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
                {chip('all', 'Toutes', counts.all)}
                {chip('todo', 'À faire', counts.unclassified + counts.toCheck)}
                {chip('unclassified', 'Sans activité', counts.unclassified)}
                {chip('toCheck', 'À vérifier', counts.toCheck)}
                {chip('verified', 'Vérifiées', counts.verified)}
                {activityFilter && (
                    <button onClick={() => setParam({ activity: null })} className="rounded-full bg-[#F5F3FF] px-3 py-1 text-[12.5px] font-semibold text-[#241C5C]">
                        {activityFilter.split('|')[1] || activityFilter.split('|')[0]} ✕
                    </button>
                )}
                {(sites.length > 1 || site) && (
                    <select value={site} onChange={(e) => setParam({ site: e.target.value || null, section: null })} aria-label="Site" className={select}>
                        <option value="">Tous les sites · {pages.length}</option>
                        {sites.map((s) => (
                            <option key={s.hostname} value={s.hostname}>
                                {s.hostname} · {s.pages}
                            </option>
                        ))}
                    </select>
                )}
                {(sections.length > 1 || section) && (
                    <select value={section} onChange={(e) => setParam({ section: e.target.value || null })} aria-label="Section" className={select}>
                        <option value="">Toutes les sections</option>
                        {sections.map(([s, n]) => (
                            <option key={s} value={s}>
                                {s === '/' ? 'Racine du site' : s} · {n}
                            </option>
                        ))}
                    </select>
                )}
                {selected.size === 0 && visible.length > 1 && (
                    <button onClick={() => setSelected(new Set(visible.map((p) => p.url)))} className={btnGhost}>
                        Sélectionner les {visible.length} pages
                    </button>
                )}
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher une page ou une activité"
                    aria-label="Rechercher"
                    className="ml-auto min-w-[220px] rounded-[9px] border border-[#DDD5C9] bg-white px-3 py-1.5 text-[13px] outline-none focus:border-[#241C5C]"
                />
            </div>

            {/* Bulk bar */}
            <div ref={bulkRef} className="-mb-4 scroll-mt-4" />
            {selected.size > 0 && (
                <div className="sticky top-2 z-10 overflow-hidden rounded-[14px] border-2 border-[#241C5C] bg-white shadow-[0_12px_32px_rgba(36,28,92,0.18)]">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-[#241C5C] px-4 py-2.5 text-white">
                        <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-white px-2 text-[12.5px] font-bold text-[#241C5C]">{selected.size}</span>
                        <strong className="text-[14px]">{selected.size > 1 ? 'pages sélectionnées' : 'page sélectionnée'}</strong>
                        {selection.scope && <span className="min-w-0 truncate font-jbmono text-[12px] text-white/70">{selection.scope}</span>}
                        <span className="flex flex-wrap gap-1.5 text-[11.5px] font-semibold">
                            {selection.unclassified > 0 && <span className="rounded-full bg-[#FBEDEB] px-2 py-[1px] text-[#A8332B]">{selection.unclassified} sans activité</span>}
                            {selection.toCheck.length > 0 && <span className="rounded-full bg-[#FCF2D4] px-2 py-[1px] text-[#7A5A05]">{selection.toCheck.length} à vérifier</span>}
                            {selection.verified > 0 && <span className="rounded-full bg-[#D9F0DE] px-2 py-[1px] text-[#146334]">{selection.verified} vérifiées</span>}
                        </span>
                        <button onClick={() => setSelected(new Set())} className="ml-auto text-[12.5px] font-semibold text-white/80 hover:text-white">
                            Tout désélectionner <span className="ml-1 rounded-[4px] border border-white/30 px-1 text-[10.5px] text-white/60">Échap</span>
                        </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 px-4 py-3">
                        <span className="text-[13px] font-semibold text-[#3B352C]">Activité à attribuer</span>
                        {renderActivitySelect(bulkActivity, setBulkActivity, 'Activité à attribuer')}
                        {suggestion && keyOf(suggestion.activity) === keyOf(bulkActivity) && (
                            <span className="rounded-full bg-[#F5F3FF] px-2 py-[2px] text-[11.5px] font-semibold text-[#241C5C]">
                                ✦ Suggérée d'après <span className="font-jbmono">{suggestion.from}</span>
                            </span>
                        )}
                        <button
                            onClick={async () => {
                                const n = selected.size;
                                if (await setActivity(Array.from(selected), bulkActivity)) {
                                    toast.success(`${activityLabel(bulkActivity)} attribuée à ${plural(n, 'page', 'pages')}`);
                                    setSelected(new Set());
                                }
                            }}
                            disabled={busy || !bulkActivity.industry}
                            className={`${btnPrimary} px-4 py-2 text-[13px]`}
                        >
                            {progress ? `Enregistrement… ${progress.done} / ${progress.total}` : `Attribuer à ${plural(selected.size, 'page', 'pages')}`}
                        </button>
                        {selection.toCheck.length > 0 && (
                            <>
                                <span className="text-[12.5px] text-[#A79E90]">ou</span>
                                <button onClick={() => confirmPages(selection.toCheck.map((p) => p.url))} disabled={busy} className={btnGhost}>
                                    Garder l'activité détectée{selection.toCheck.length < selected.size ? ` (${selection.toCheck.length})` : ''}
                                </button>
                            </>
                        )}
                    </div>
                    {progress && (
                        <div className="h-1 bg-[#F1ECE4]">
                            <div className="h-full bg-[#241C5C] transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
                        </div>
                    )}
                    {selection.hidden > 0 && (
                        <p className="border-t border-[#EDE7DE] bg-[#FAF8F4] px-4 py-1.5 text-[12px] text-[#8A8172]">
                            Dont {plural(selection.hidden, 'page non affichée', 'pages non affichées')} dans la liste (autre filtre ou plus bas que les pages affichées).
                        </p>
                    )}
                </div>
            )}

            {/* List */}
            {pages.length === 0 ? (
                <div className="rounded-[16px] border border-dashed border-[#DDD5C9] bg-[#FAF8F4] p-8 text-center text-[14px] text-[#6B6357]">
                    <strong className="text-[#1A1613]">Aucune page pour l'instant.</strong>
                    <br />
                    Elles apparaissent dès que le formulaire est affiché sur votre site. Vous pouvez aussi les ajouter vous-même.
                    <div className="mt-3">
                        <button onClick={() => setParam({ add: '1' })} className={btnPrimary}>
                            + Ajouter une page
                        </button>
                    </div>
                </div>
            ) : visible.length === 0 ? (
                <p className="rounded-[14px] border border-[#EDE7DE] bg-white px-4 py-6 text-center text-[13.5px] text-[#8A8172]">Aucune page ne correspond.</p>
            ) : (
                <div className="overflow-hidden rounded-[16px] border border-[#EDE7DE] bg-white">
                    <div className="hidden grid-cols-[28px_1.6fr_1.5fr_0.7fr_0.7fr_0.6fr_28px] items-center gap-3 border-b border-[#EDE7DE] bg-[#FAF8F4] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#A79E90] lg:grid">
                        <input
                            type="checkbox"
                            aria-label="Tout sélectionner"
                            className={checkbox}
                            ref={(el) => {
                                if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected;
                            }}
                            checked={allVisibleSelected}
                            onChange={() =>
                                setSelected(allVisibleSelected ? new Set() : new Set(visible.slice(0, limit).map((p) => p.url)))
                            }
                        />
                        <span>Page</span>
                        <span>Activité</span>
                        <span>Prix</span>
                        <span>Formulaire</span>
                        <span>Leads 30 j</span>
                        <span />
                    </div>
                    {allVisibleSelected && visible.length > limit && (
                        <div className="border-b border-[#E4DEF7] bg-[#F5F3FF] px-4 py-2 text-center text-[12.5px] text-[#241C5C]">
                            {wholeViewSelected ? (
                                <>
                                    Les {visible.length} pages de cette vue sont sélectionnées.{' '}
                                    <button onClick={() => setSelected(new Set())} className="font-semibold underline">
                                        Tout désélectionner
                                    </button>
                                </>
                            ) : (
                                <>
                                    Les {Math.min(limit, visible.length)} pages affichées sont sélectionnées.{' '}
                                    <button onClick={() => setSelected(new Set(visible.map((p) => p.url)))} className="font-semibold underline">
                                        Sélectionner les {visible.length} pages de cette vue
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                    {visible.slice(0, limit).map((p) => {
                        const price = p.industry ? priceBy.get(keyOf(p)) : undefined;
                        return (
                            <div
                                key={p.url}
                                className={`grid grid-cols-[28px_1fr] items-center gap-x-3 gap-y-1.5 border-b border-[#F1ECE4] px-4 py-2.5 last:border-0 lg:grid-cols-[28px_1.6fr_1.5fr_0.7fr_0.7fr_0.6fr_28px] ${
                                    selected.has(p.url) ? 'bg-[#F5F3FF]' : isUnclassified(p) ? 'bg-[#FFF8F7]' : ''
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    aria-label={`Sélectionner ${pathOf(p.url)}`}
                                    className={checkbox}
                                    checked={selected.has(p.url)}
                                    onChange={() =>
                                        setSelected((s) => {
                                            const next = new Set(s);
                                            next.has(p.url) ? next.delete(p.url) : next.add(p.url);
                                            return next;
                                        })
                                    }
                                />
                                <span className="min-w-0">
                                    <a href={p.url} target="_blank" rel="noreferrer" className="block truncate font-jbmono text-[12.5px] text-[#1A1613] hover:underline" title={p.url}>
                                        {pathOf(p.url)}
                                    </a>
                                    <span className="flex flex-wrap items-center gap-1.5">
                                        <StatusChip page={p} />
                                        {!site && <span className="text-[11.5px] text-[#A79E90]">{p.hostname}</span>}
                                    </span>
                                </span>
                                <span className="col-start-2 flex flex-wrap items-center gap-1.5 lg:col-start-auto">
                                    {editing?.url === p.url ? (
                                        <>
                                            {renderActivitySelect(editing.draft, (a) => setEditing({ url: p.url, draft: a }), `Activité de ${pathOf(p.url)}`)}
                                            <button
                                                onClick={async () => {
                                                    if (await setActivity([p.url], editing.draft)) setEditing(null);
                                                }}
                                                disabled={busy || !editing.draft.industry}
                                                className={btnPrimary}
                                            >
                                                Enregistrer
                                            </button>
                                            <button onClick={() => setEditing(null)} className="text-[12.5px] font-semibold text-[#8A8172] hover:text-[#3B352C]">
                                                Annuler
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => setEditing({ url: p.url, draft: { industry: p.industry || '', subIndustry: p.subIndustry || '' } })}
                                                className={`max-w-full truncate rounded-[8px] border px-2.5 py-1 text-left text-[13px] font-semibold transition-colors hover:border-[#241C5C] ${
                                                    p.industry ? 'border-[#EDE7DE] bg-white text-[#1A1613]' : 'border-[#F2C9C4] bg-white text-[#A8332B]'
                                                }`}
                                                title={p.industry ? `${p.industry}${p.subIndustry ? ` · ${p.subIndustry}` : ''}` : undefined}
                                            >
                                                {p.industry ? activityLabel(p) : 'Choisir une activité'} <span className="text-[#A79E90]">▾</span>
                                            </button>
                                            {isToCheck(p) && (
                                                <button onClick={() => confirmPages([p.url])} disabled={busy} className={btnPrimary}>
                                                    C'est bon
                                                </button>
                                            )}
                                        </>
                                    )}
                                </span>
                                <span className="col-start-2 text-[12.5px] lg:col-start-auto">
                                    {!p.industry ? (
                                        <span className="text-[#A79E90]">—</span>
                                    ) : price?.price != null ? (
                                        <>
                                            <span className="inline-flex items-center gap-1">
                                                <strong>{price.price} €</strong>
                                                <button
                                                    onClick={onOpenPrices}
                                                    aria-label="Modifier le prix"
                                                    title="Modifier le prix"
                                                    className="rounded-[5px] p-0.5 text-[#A79E90] transition-colors hover:bg-[#F1ECE4] hover:text-[#241C5C]"
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                        <path d="M12 20h9" />
                                                        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                                    </svg>
                                                </button>
                                            </span>
                                            <span className="block text-[11px] text-[#8A8172]">{price.source === 'integration' ? 'prix propre' : 'prix du compte'}</span>
                                        </>
                                    ) : (
                                        <button onClick={onOpenPrices} className="font-semibold text-[#7A5A05] hover:underline">
                                            Fixer le prix
                                        </button>
                                    )}
                                </span>
                                <span className="col-start-2 text-[12.5px] text-[#6B6357] lg:col-start-auto">
                                    {p.views > 0 ? `✓ ${plural(p.views, 'vue', 'vues')}` : p.lastVisitAt ? `Visitée ${timeAgo(p.lastVisitAt)}` : '—'}
                                </span>
                                <span className="col-start-2 text-[12.5px] lg:col-start-auto">{p.leads30 ? `${p.leads30}` : '—'}</span>
                                <span className="relative col-start-2 lg:col-start-auto">
                                    <button
                                        onClick={() => setMenuFor(menuFor === p.url ? null : p.url)}
                                        aria-label={`Actions pour ${pathOf(p.url)}`}
                                        className="rounded-[7px] border border-[#DDD5C9] bg-white px-1.5 py-[1px] text-[13px] font-semibold text-[#3B352C] hover:border-[#241C5C]"
                                    >
                                        ⋯
                                    </button>
                                    {menuFor === p.url && (
                                        <span className="absolute right-0 top-[calc(100%+4px)] z-20 flex min-w-[190px] flex-col rounded-[10px] border border-[#EDE7DE] bg-white p-1 shadow-[0_12px_30px_rgba(20,17,14,0.14)]">
                                            <a href={p.url} target="_blank" rel="noreferrer" onClick={() => setMenuFor(null)} className="rounded-[7px] px-3 py-2 text-left text-[13px] font-semibold hover:bg-[#FAF8F4]">
                                                Ouvrir la page ↗
                                            </a>
                                            <button
                                                onClick={() => {
                                                    setMenuFor(null);
                                                    removePage(p);
                                                }}
                                                className="rounded-[7px] px-3 py-2 text-left text-[13px] font-semibold text-[#A8332B] hover:bg-[#FAF8F4]"
                                            >
                                                Retirer la page
                                            </button>
                                        </span>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                    {visible.length > limit && (
                        <button onClick={() => setLimit((l) => l + 50)} className="w-full px-4 py-2.5 text-[13px] font-semibold text-[#241C5C] hover:bg-[#FAF8F4]">
                            Afficher {Math.min(50, visible.length - limit)} pages de plus
                        </button>
                    )}
                </div>
            )}

            {/* Review mode */}
            {reviewing && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(20,17,14,0.42)' }}>
                    <div role="dialog" aria-modal="true" aria-label="Vérifier les pages une à une" className="w-full max-w-[560px] rounded-[18px] bg-white p-6 shadow-[0_24px_60px_rgba(20,17,14,0.22)]">
                        {current ? (
                            <>
                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-[12px] font-bold uppercase tracking-[0.07em] text-[#A79E90]">
                                        Page {Math.min(reviewIndex + 1, reviewTotal || 1)} / {reviewTotal || pending.length}
                                    </span>
                                    <button onClick={() => setParam({ review: null })} className="text-[13px] font-semibold text-[#8A8172] hover:text-[#3B352C]">
                                        Fermer
                                    </button>
                                </div>
                                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#F1ECE4]">
                                    <div className="h-full rounded-full bg-[#241C5C] transition-all" style={{ width: `${reviewTotal ? (reviewIndex / reviewTotal) * 100 : 0}%` }} />
                                </div>
                                <h3 className="mt-5 break-all font-jbmono text-[15px] text-[#14110E]">{pathOf(current.url)}</h3>
                                <p className="mt-1 text-[12.5px] text-[#8A8172]">
                                    {current.hostname} ·{' '}
                                    <a href={current.url} target="_blank" rel="noreferrer" className="font-semibold text-[#241C5C] hover:underline">
                                        Ouvrir la page ↗
                                    </a>
                                    {current.views > 0 ? ` · ${plural(current.views, 'vue du formulaire', 'vues du formulaire')}` : ''}
                                </p>
                                <p className="mt-4 text-[13.5px] text-[#3B352C]">
                                    {current.industry ? (
                                        <>
                                            Activité détectée : <strong>{activityLabel(current)}</strong>. Est-ce bien ce que vend cette page ?
                                        </>
                                    ) : (
                                        <strong className="text-[#A8332B]">Aucune activité détectée : choisissez ce que vend cette page.</strong>
                                    )}
                                </p>
                                <div className="mt-2">{renderActivitySelect(reviewDraft, setReviewDraft, 'Activité de la page')}</div>
                                <div className="mt-5 flex flex-wrap items-center gap-2">
                                    <button onClick={reviewConfirm} disabled={busy || !reviewDraft.industry} className={`${btnPrimary} px-4 py-2.5 text-[13.5px]`}>
                                        {current.industry && keyOf(reviewDraft) === keyOf(current) ? "C'est bon" : 'Enregistrer'} <span className="opacity-60">↵</span>
                                    </button>
                                    <button onClick={reviewSkip} className={`${btnGhost} px-4 py-2.5 text-[13.5px]`}>
                                        Passer <span className="opacity-60">→</span>
                                    </button>
                                    <span className="ml-auto text-[12px] text-[#A79E90]">{plural(reviewDone, 'page vérifiée', 'pages vérifiées')}</span>
                                </div>
                            </>
                        ) : (
                            <div className="text-center">
                                <h3 className="font-newsreader text-[24px] font-medium text-[#14110E]">
                                    {skipped.size ? 'Vous avez tout passé en revue' : 'Tout est vérifié'}
                                </h3>
                                <p className="mt-2 text-[14px] text-[#6B6357]">
                                    {plural(reviewDone, 'page vérifiée', 'pages vérifiées')}
                                    {skipped.size ? ` · ${plural(skipped.size, 'page passée', 'pages passées')}, toujours dans « À faire »` : ''}.
                                </p>
                                <button onClick={() => setParam({ review: null })} className={`${btnPrimary} mt-4 px-4 py-2.5 text-[13.5px]`}>
                                    Terminer
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Add pages */}
            {adding && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(20,17,14,0.42)' }} onClick={() => setParam({ add: null })}>
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Ajouter une page"
                        onClick={(e) => e.stopPropagation()}
                        className="flex w-full max-w-[520px] flex-col gap-3.5 rounded-[18px] bg-white p-6 shadow-[0_24px_60px_rgba(20,17,14,0.22)]"
                    >
                        <h3 className="font-newsreader text-[24px] font-medium text-[#14110E]">Ajouter une page</h3>
                        {sites.length > 0 && (
                            <label className="flex flex-col gap-1 text-[13px] font-semibold text-[#3B352C]">
                                Site
                                <select value={addSite} onChange={(e) => setAddSite(e.target.value)} className={select}>
                                    {sites.map((s) => (
                                        <option key={s.hostname} value={s.hostname}>
                                            {s.hostname}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label className="flex flex-col gap-1 text-[13px] font-semibold text-[#3B352C]">
                            Adresse de la page
                            <textarea
                                autoFocus
                                rows={3}
                                value={addText}
                                onChange={(e) => setAddText(e.target.value)}
                                placeholder={addSite ? `/pompe-a-chaleur\nou https://${addSite}/devis` : 'https://exemple.fr/pompe-a-chaleur'}
                                className="rounded-[10px] border border-[#DDD5C9] px-3 py-2 font-jbmono text-[12.5px] font-normal outline-none focus:border-[#241C5C]"
                            />
                            <span className="text-[12px] font-normal text-[#8A8172]">Une page par ligne : collez-en plusieurs pour les ajouter d'un coup.</span>
                        </label>
                        <div className="flex flex-col gap-1 text-[13px] font-semibold text-[#3B352C]">
                            Ce que vend cette page
                            {renderActivitySelect(addActivity, setAddActivity, 'Activité des pages ajoutées')}
                        </div>
                        {addUrls.length > 0 && (
                            <p className="text-[12.5px] text-[#6B6357]">
                                {plural(addUrls.length, 'page sera ajoutée', 'pages seront ajoutées')}, déjà vérifiée{addUrls.length > 1 ? 's' : ''} :{' '}
                                <span className="font-jbmono">{addUrls.slice(0, 3).map(pathOf).join(', ')}{addUrls.length > 3 ? '…' : ''}</span>
                            </p>
                        )}
                        <div className="mt-1 flex justify-end gap-2">
                            <button onClick={() => setParam({ add: null })} className={btnGhost}>
                                Annuler
                            </button>
                            <button onClick={addPages} disabled={busy || !addActivity.industry || addUrls.length === 0} className={btnPrimary}>
                                {busy ? 'Ajout…' : addUrls.length > 1 ? `Ajouter ${addUrls.length} pages` : 'Ajouter la page'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PagesTab;
